import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { apiClient } from "../lib/api";
import { useCart } from "./CartContext";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";

/**
 * Polls the backend for Stripe checkout status after the user returns from Stripe.
 * Clears the cart on successful payment.
 */
export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ phase: "polling", data: null, message: "" });
  const { clear } = useCart();

  useEffect(() => {
    if (!sessionId) {
      setState({ phase: "error", data: null, message: "Missing session_id in URL." });
      return;
    }

    let attempts = 0;
    let cancelled = false;
    const max = 10;
    const interval = 2500;

    const poll = async () => {
      if (cancelled) return;
      if (attempts >= max) {
        setState({
          phase: "timeout",
          data: null,
          message: "Still processing. Please check your email shortly.",
        });
        return;
      }
      attempts += 1;
      try {
        const { data } = await apiClient.get(`/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState({ phase: "paid", data, message: "" });
          clear();
          return;
        }
        if (data.status === "expired") {
          setState({ phase: "expired", data, message: "Checkout expired." });
          return;
        }
        setTimeout(poll, interval);
      } catch (e) {
        setTimeout(poll, interval);
      }
    };
    poll();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const amount =
    state.data && state.data.amount_total ? state.data.amount_total / 100 : null;
  const items = (state.data && state.data.line_items) || [];
  const curSymbol = (state.data?.currency || "cad").toLowerCase() === "usd" ? "$" : "C$";

  return (
    <div className="max-w-2xl mx-auto px-6 py-32 text-center" data-testid="checkout-success-page">
      {state.phase === "polling" && (
        <>
          <Loader2 size={48} className="mx-auto mb-6 animate-spin text-zinc-400" strokeWidth={1.5} />
          <h1 className="text-3xl font-black mb-2" style={{ fontFamily: "Outfit" }}>
            Confirming your payment…
          </h1>
          <p className="text-sm text-zinc-500">This usually takes a few seconds.</p>
        </>
      )}
      {state.phase === "paid" && (
        <>
          <CheckCircle2 size={48} className="mx-auto mb-6 text-emerald-500" strokeWidth={1.5} />
          <div className="text-[10px] uppercase tracking-[0.3em] font-semibold text-zinc-500 mb-3">
            Order Confirmed
          </div>
          <h1
            className="text-4xl md:text-5xl font-black tracking-tighter mb-4"
            style={{ fontFamily: "Outfit" }}
          >
            Thank you.
          </h1>
          <p className="text-zinc-600 max-w-md mx-auto leading-relaxed mb-8">
            We&apos;ve received your order{amount ? ` of $${amount.toFixed(2)}` : ""}. A receipt is on its
            way to your inbox and tracking info will follow when your items ship.
          </p>

          {items.length > 0 && (
            <div
              className="max-w-md mx-auto text-left border border-zinc-200 divide-y divide-zinc-100 mb-8"
              data-testid="order-recap"
            >
              {items.map((it, i) => (
                <div key={i} className="flex items-start justify-between gap-4 p-4" data-testid={`recap-item-${i}`}>
                  <div className="min-w-0">
                    <div className="font-bold text-sm tracking-tight" style={{ fontFamily: "Outfit" }}>
                      {it.quantity}× {it.name}
                    </div>
                    <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1">
                      {it.color && (
                        <span
                          className="text-[10px] uppercase tracking-[0.2em] font-semibold text-zinc-700"
                          data-testid={`recap-color-${i}`}
                        >
                          Colour · {it.color}
                        </span>
                      )}
                      {it.fragrance && (
                        <span
                          className="text-[10px] uppercase tracking-[0.2em] font-semibold luxe-text"
                          data-testid={`recap-fragrance-${i}`}
                        >
                          {it.fragrance === "Unscented" ? "Unscented" : `Scent · ${it.fragrance}`}
                        </span>
                      )}
                    </div>
                  </div>
                  {it.unit_price != null && (
                    <div className="text-sm font-mono text-zinc-500 shrink-0">
                      {curSymbol}{(it.unit_price * it.quantity).toFixed(2)}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          <Link
            to="/"
            className="inline-flex items-center gap-2 bg-zinc-900 text-white px-8 py-3 text-xs uppercase tracking-[0.25em] font-bold hover:bg-black transition"
          >
            Continue Shopping
          </Link>
        </>
      )}
      {(state.phase === "timeout" || state.phase === "expired" || state.phase === "error") && (
        <>
          <XCircle size={48} className="mx-auto mb-6 text-amber-500" strokeWidth={1.5} />
          <h1 className="text-3xl font-black mb-3" style={{ fontFamily: "Outfit" }}>
            {state.phase === "expired" ? "Checkout expired" : "Still processing"}
          </h1>
          <p className="text-sm text-zinc-500 mb-8">{state.message}</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 border border-zinc-900 text-zinc-900 px-8 py-3 text-xs uppercase tracking-[0.25em] font-bold hover:bg-zinc-100"
          >
            Back to shop
          </Link>
        </>
      )}
    </div>
  );
}
